import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { works } from "@/mocks/works";
import { news } from "@/mocks/news";
import { characters } from "@/mocks/characters";
import HeroSlideshow from "./components/HeroSlideshow";
import WorkCard from "@/components/feature/WorkCard";
import NewsCard from "@/components/feature/NewsCard";
import CharacterCard from "@/components/feature/CharacterCard";
import SectionHeading from "@/components/feature/SectionHeading";

const entries = [
  {
    to: "/works",
    icon: "ri-film-line",
    title: "影视作品",
    desc: "按类型与年份浏览全部作品，查看剧情、主创与角色信息。",
    count: works.length,
  },
  {
    to: "/news",
    icon: "ri-newspaper-line",
    title: "动态资讯",
    desc: "作品上映、预告释出、评选提名等最新影视动态与专题报道。",
    count: news.length,
  },
  {
    to: "/characters",
    icon: "ri-user-star-line",
    title: "角色解析",
    desc: "深入每一部作品中的角色，理解其背景、台词与剧情表现。",
    count: characters.length,
  },
];

export default function Home() {
  useSeo({
    title: "希岛爱理影视作品介绍与资讯平台",
    description:
      "希岛爱理官方网站，提供丰富的影视作品介绍、动态资讯和角色解析，满足影迷对希岛爱理相关内容的全面了解和体验。",
    keywords: "希岛爱理,影视作品,演员资料,影视资讯,电影介绍,电视剧,影视动态,角色解析",
  });

  const slides = works.slice(0, 4);
  const featured = works.slice(0, 6);
  const latestNews = news.slice(0, 4);
  const topicCharacters = characters.slice(0, 4);

  return (
    <>
      <HeroSlideshow slides={slides} />

      {/* 分类快捷入口 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {entries.map((entry) => (
            <Link
              key={entry.to}
              to={entry.to}
              className="group flex flex-col p-6 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-500 text-background-50 mb-4">
                <i className={`${entry.icon} text-2xl`} />
              </div>
              <div className="flex items-center gap-2">
                <h2 className="font-heading font-bold text-lg text-foreground-950">{entry.title}</h2>
                <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">
                  {entry.count} 项
                </span>
              </div>
              <p className="text-sm text-foreground-600 mt-2 leading-relaxed flex-1">{entry.desc}</p>
              <span className="mt-4 text-sm font-medium text-accent-600 inline-flex items-center gap-1">
                进入栏目
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 精选影视作品 */}
      <section className="w-full px-4 md:px-8 pb-12 md:pb-16">
        <SectionHeading
          eyebrow="Featured Works"
          title="精选影视作品推荐"
          description="从经典到最新，挑选口碑与话题度俱佳的代表作品，快速了解希岛爱理的影视轨迹。"
          action={
            <Link
              to="/works"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              全部作品
              <i className="ri-arrow-right-line" />
            </Link>
          }
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
          {featured.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      </section>

      {/* 最新资讯 + 角色专题 */}
      <section className="w-full px-4 md:px-8 pb-12 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <SectionHeading
              eyebrow="Latest News"
              title="最新影视资讯"
              action={
                <Link
                  to="/news"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  更多资讯
                  <i className="ri-arrow-right-line" />
                </Link>
              }
            />
            <div className="flex flex-col gap-4">
              {latestNews.map((item) => (
                <NewsCard key={item.id} item={item} layout="row" />
              ))}
            </div>
          </div>

          <div>
            <SectionHeading eyebrow="Characters" title="角色解析专题" />
            <div className="rounded-lg bg-primary-950 p-6 text-background-100">
              <p className="text-sm text-background-300 leading-relaxed">
                每个角色都是一段独立的人生。我们整理了希岛爱理在不同作品中的角色背景、代表台词与剧情片段，帮助你更立体地理解每一次表演。
              </p>
              <div className="grid grid-cols-2 gap-3 mt-5">
                {topicCharacters.map((c) => (
                  <Link
                    key={c.id}
                    to={`/characters/${c.id}`}
                    className="group rounded-md overflow-hidden bg-primary-900 border border-primary-800 hover:border-accent-400 transition-colors cursor-pointer"
                  >
                    <div className="w-full aspect-[4/5] overflow-hidden">
                      <img
                        src={c.image}
                        alt={`${c.name} 角色形象`}
                        title={`${c.name} - 希岛爱理角色解析`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="px-2.5 py-2">
                      <p className="text-sm font-medium text-background-50">{c.name}</p>
                      <p className="text-[11px] text-background-400 truncate">《{c.workTitle}》</p>
                    </div>
                  </Link>
                ))}
              </div>
              <Link
                to="/characters"
                className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-accent-400 hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                进入角色解析专题
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 网站简介 */}
      <section className="w-full px-4 md:px-8">
        <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <span className="text-xs font-label font-semibold tracking-[0.2em] uppercase text-accent-600">
              About The Site
            </span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-2 mb-3">
              关于本站
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed">
              本站围绕日本演员希岛爱理，系统整理其影视作品、动态资讯与角色解析三类内容。无论是想快速浏览作品列表，还是深入了解某个角色的背景与台词，都可以在这里找到线索。当前页面内容为演示数据，用于展示站点的浏览、筛选与搜索体验。
            </p>
            <Link
              to="/about"
              className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              了解更多
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-1 gap-4 lg:gap-3">
            <div className="rounded-md bg-background-50 border border-background-200 p-4">
              <p className="font-heading text-2xl font-bold text-primary-700">{works.length}</p>
              <p className="text-xs text-foreground-500 mt-1">影视作品</p>
            </div>
            <div className="rounded-md bg-background-50 border border-background-200 p-4">
              <p className="font-heading text-2xl font-bold text-accent-600">{characters.length}</p>
              <p className="text-xs text-foreground-500 mt-1">角色解析</p>
            </div>
            <div className="rounded-md bg-background-50 border border-background-200 p-4">
              <p className="font-heading text-2xl font-bold text-secondary-600">{news.length}</p>
              <p className="text-xs text-foreground-500 mt-1">资讯动态</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}